import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { FONT_COLOR } from '../const/general'
import { Typography } from '@mui/material';
import Progress from '../item/progress'

export default function RiskAnalysisLine(props) {

    const [lineData, setLineData] = useState([]);
    const [linePoints, setLinePoints] = useState([]);
    const [categoryDates, setCategoryDates] = useState([]);
    const [seriesMax, setSeriesMax] = useState(0);
    const [isNoData, setIsNoData] = useState(false);
    const [colorChartData, setColorChartData] = useState(null);

    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    // チャート情報を格納
    const setChartData = (date_ary, chartData, colorData) => {

        setCategoryDates(date_ary);
        setLineData(chartData)

        if (chartData.length > 0) {
            // 河川水位、最大値を取得
            setSeriesMax(props.limitVal + 1)
            if (chartData.length > 0 && chartData.reduce(aryMax) > props.limitVal) {
                setSeriesMax(chartData.reduce(aryMax))
            }
            let points = []
            for (let i = 0; i < chartData.length; i++) {
                // 土砂災害発生リスク、マーカーの色を取得
                const colorLevel = colorData?.length > 0 ? colorData[i] : chartData[i];
                const color = props.setColor(colorLevel);
                const point = {
                    x: date_ary[i],
                    y: chartData[i],
                    marker: {
                        size: 4,
                        fillColor: color,
                        strokeColors: 'transparent',
                        strokeWidth: 0,
                        shape: "circle",
                    }
                }
                points.push(point)
            }
            setLinePoints(points)
        }
    }

    // スライダーにより選択されている時刻を青色表示（色は仮）
    const labelColors = categoryDates.map((cate, i) =>
        i === props.chooseNum ? '#0000FF' : FONT_COLOR
    );

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    useEffect(() => {
        if (props.dateArray === null ||
            props.chartData === null) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.chartData && props.chartData.length > 0) {
            setChartData(props.dateArray, props.chartData, props.colorChartData)
            setColorChartData(props.colorChartData)
        }
        setIsNoData(false)
        loadingEnd()
    }, [props.dateArray, props.chartData, props.colorChartData]);

    useEffect(() => {
        if (props.dateArray === null ||
            props.chartData === null) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.chartData && props.chartData.length > 0) {
            setChartData(props.dateArray, props.chartData, props.colorChartData)
            setColorChartData(props.colorChartData)
        }
        setIsNoData(false)
        loadingEnd()

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const series = useMemo(() => {
        return [
            {
                name: props.dataName,
                type: 'line',
                data: lineData
            },
        ];
    }, [lineData]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
                offsetY: 0
            },
            type: "line",
            stacked: false,
            zoom: {
                enabled: false
            }
        },
        // ポイントの数値表示
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0],
            offsetY: -5,
            style: {
                fontSize: '10px',
                colors: ['white'],
                background: 'transparent'
            },
            background: {
                enabled: false
            },
        },
        stroke: {
            width: [2, 0],
            colors: ['#5c9fed',
                'transparent']
        },
        markers: {
            size: [0],
            strokeColors: ['#a9a9a9'],
            strokeWidth: [1],
            hover: {
                size: [3],
                sizeOffset: 0
            }
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    position: 'top', // top, center, bottom
                },
            }
        },
        annotations: {
            yaxis: props.annotations,
            xaxis: [
                {
                    x: categoryDates[12],
                    borderColor: '#0000FF',
                    borderWidth: 1,
                    strokeDashArray: 4,
                    label: {
                        show: false,
                    }
                }
            ],
            points: linePoints
        },
        xaxis: {
            categories: categoryDates,
            labels: {
                rotate: -60,
                rotateAlways: true,
                style: {
                    colors: labelColors,
                    fontSize: '10px',
                },
            }
        },
        yaxis: [
            {
                opposite: true,
                min: 0,
                max: seriesMax,
                seriesName: props.dataName,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false,
                },
                labels: {
                    show: false,
                },
                lines: {
                    show: false
                },
            },
        ],
        tooltip: {
            x: {
                show: false
            },
            y: {
                formatter: function (val) {
                    return parseFloat(val).toFixed(0); // ← 小数なしを明示的に指定
                }
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const colorLevel = colorChartData?.length > 0 ? colorChartData[dataPointIndex] : null
                const val =  series[seriesIndex][dataPointIndex];
                const color = props.setColor(colorLevel ?? val);
                const name = w.config.series[seriesIndex].name;
                return `
                  <div style="padding:5px;">
                    <span style="display:inline-block;width:10px;height:10px;border-radius:5px;background:${color};margin-right:5px;"></span>
                    ${name}: ${val}
                  </div>
                `;
            }
        },
        grid: {
            show: false, // 全体の罫線を消す
            padding: {
                left: 22,
                right: 12,
                top: -10,
                bottom: -13
            }
        },
        legend: {
            show: false,
            height: 0,
        }
    }), [categoryDates, seriesMax, props.annotations, props.dataName, labelColors]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (lineData && lineData.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            {!props.isReadyFlg ?
                <Progress />
                // <ChartSkeleton />
                :
                isNoData ?
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'white',
                        }}
                    >
                        対象データがありません
                    </Typography>
                    :
                    <Chart
                        options={options}
                        series={series}
                        type="line"
                        height="100%"
                        width="100%"
                        style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                    />
            }
        </>
    );
}
